import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CitePopup, wrapWithCitations } from '../UI/Citations';
import {
  FaTimes,
  FaDownload,
  FaBookmark,
  FaRegBookmark,
  FaPrint,
  FaFileCode,
  FaFileAlt,
  FaCalendarAlt,
  FaUser,
} from 'react-icons/fa';
import {
  TechButton,
  TechButtonGhost,
  TechChip,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import { CATEGORY_META } from './data';
import { downloadMarkdown, downloadJSON, printItem } from './download';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.985); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  background:
    radial-gradient(ellipse at center, rgba(0, 180, 216, 0.08), transparent 60%),
    rgba(11, 19, 32, 0.78);
  backdrop-filter: blur(6px);
  display: grid;
  place-items: center;
  padding: 1.5rem 1rem;
  animation: ${fadeIn} 0.18s ease;
`;

const Modal = styled.article`
  position: relative;
  width: 100%;
  max-width: 820px;
  max-height: 88vh;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.22s ease;

  &::before, &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
    z-index: 2;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const Header = styled.header`
  position: relative;
  padding: 1.1rem 1.4rem;
  border-bottom: 1px solid ${borderSoft};
  background:
    linear-gradient(135deg, rgba(0, 180, 216, 0.08), transparent 55%),
    rgba(36, 52, 77, 0.7);
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid ${borderSoft};
  background: rgba(11, 19, 32, 0.5);
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: color 0.18s ease, border-color 0.18s ease;

  &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
`;

const Title = styled.h2`
  margin: 0 0 0.6rem;
  color: var(--text-primary);
  font-size: 1.35rem;
  letter-spacing: -0.005em;
  padding-right: 2.5rem;
  line-height: 1.3;
`;

const Meta = styled.div`
  display: flex;
  gap: 0.85rem;
  flex-wrap: wrap;
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  svg { color: ${accent}; font-size: 0.78rem; }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 0.7rem;
`;

const Tag = styled.span`
  font-family: ${mono};
  font-size: 0.66rem;
  color: #cfe9f4;
  background: rgba(0, 180, 216, 0.06);
  border: 1px solid rgba(0, 180, 216, 0.22);
  border-radius: 3px;
  padding: 1px 6px;

  &::before { content: '#'; color: ${accent}; opacity: 0.85; margin-right: 1px; }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.25rem 1.4rem;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.16);
  }
`;

const Excerpt = styled.div`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 4px;
  padding: 0.85rem 1rem;
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.55;
  margin-bottom: 1.1rem;
  font-style: italic;
`;

const Markdown = styled.div`
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 0.94rem;

  h1, h2, h3, h4 {
    color: var(--text-primary);
    margin: 1.1em 0 0.4em;
    font-weight: 600;
    line-height: 1.3;
  }
  h2 { font-size: 1.05em; letter-spacing: 0.02em; }
  h3 { font-size: 0.95em; text-transform: uppercase; letter-spacing: 0.1em; color: ${accent}; }

  p { margin: 0 0 0.75em; }
  ul, ol { margin: 0 0 0.85em 1.2em; padding: 0; }
  li { margin-bottom: 0.25em; }
  strong { color: var(--text-primary); }
  em { color: var(--text-primary); font-style: italic; }
  blockquote {
    border-left: 2px solid ${accent};
    padding-left: 0.85em;
    margin: 0.6em 0;
    color: var(--text-disabled);
  }
  code {
    font-family: ${mono};
    font-size: 0.88em;
    padding: 1px 5px;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 3px;
    color: #cfe9f4;
  }
  pre {
    font-family: ${mono};
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid ${borderSoft};
    border-radius: 6px;
    padding: 0.75em;
    overflow-x: auto;
    margin: 0.5em 0;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.7em 0;
    font-size: 0.88em;
  }
  th, td {
    padding: 5px 9px;
    border: 1px solid ${borderSoft};
    text-align: left;
  }
  th {
    background: rgba(255, 255, 255, 0.04);
    font-family: ${mono};
    font-size: 0.7em;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
  }
`;

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
  padding: 0.85rem 1.4rem;
  border-top: 1px solid ${borderSoft};
  background: ${panelDeep};
  flex-wrap: wrap;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const ResearchViewer = ({ item, saved, onClose, onToggleSave }) => {
  const [activeCite, setActiveCite] = useState(null);
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (activeCite) setActiveCite(null);
        else onClose?.();
      }
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, activeCite]);

  if (!item) return null;
  const cat = CATEGORY_META[item.category] || { label: item.category, color: accent };
  const citations = Array.isArray(item.citations) ? item.citations : [];
  const handleCite = (cite) => setActiveCite(cite);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <Backdrop onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-label={item.title}>
      <Modal>
        <Header>
          <Eyebrow>
            <TechChip>{item.type}</TechChip>
            <span style={{
              fontFamily: mono,
              fontSize: '0.62rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              padding: '2px 7px',
              color: cat.color,
              border: `1px solid ${cat.color}55`,
              background: 'rgba(0, 180, 216, 0.04)',
              borderRadius: 3,
            }}>{cat.label}</span>
            {typeof item.pages === 'number' && (
              <TechChip tone="mute">{item.pages} págs</TechChip>
            )}
          </Eyebrow>
          <Title>{item.title}</Title>
          <Meta>
            <span><FaCalendarAlt />{new Date(item.date).toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
            <span><FaUser />{item.author}</span>
            <span><FaFileAlt />ID #{item.id}</span>
          </Meta>
          <TagRow>
            {(item.tags || []).map((t) => <Tag key={t}>{t}</Tag>)}
          </TagRow>
          <CloseBtn onClick={onClose} aria-label="Cerrar">
            <FaTimes />
          </CloseBtn>
        </Header>

        <Body>
          <Excerpt>{item.excerpt}</Excerpt>
          {item.body
            ? (
              <Markdown>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={wrapWithCitations(handleCite)}
                >
                  {item.body}
                </ReactMarkdown>
              </Markdown>
            )
            : <p style={{ color: 'var(--text-disabled)' }}>Este estudio aún no tiene contenido extendido.</p>
          }
        </Body>

        <Footer>
          <TechButtonGhost type="button" onClick={() => onToggleSave?.(item.id)}>
            {saved ? <FaBookmark /> : <FaRegBookmark />}
            {saved ? 'Guardado' : 'Guardar'}
          </TechButtonGhost>

          <ActionGroup>
            <TechButtonGhost type="button" onClick={() => printItem(item)}>
              <FaPrint /> Imprimir / PDF
            </TechButtonGhost>
            <TechButtonGhost type="button" onClick={() => downloadJSON(item)}>
              <FaFileCode /> JSON
            </TechButtonGhost>
            <TechButton type="button" onClick={() => downloadMarkdown(item)}>
              <FaDownload /> Markdown
            </TechButton>
          </ActionGroup>
        </Footer>
      </Modal>
      {activeCite && (
        <CitePopup
          cite={activeCite}
          indexed={citations}
          fresh={[]}
          rag={[]}
          onClose={() => setActiveCite(null)}
        />
      )}
    </Backdrop>
  );
};

export default ResearchViewer;
